import {CommerceAPI, ShopperProducts} from 'commerce-api'
import type {RootStore} from './RootStore'
import {makeObservable, flow, observable, action, flowResult} from 'mobx'
import {OmitFromKnownKeys, StoreModel, Wrapper} from './utils'
import {keysToCamel} from '../utils/utils'
import { BaseItem } from "@algolia/autocomplete-core";
import { Nullable } from '../types/utils'
import { RetailMediaProduct, RetailMediaResponse, RetailMediaSponsoredProductResponse, SponsoredProductRequestProps, SponsoredHit } from '../types/retail-media'
import { AlgoliaResponse, OrganicProductFetchResult } from '../types/algolia'
import { HITS_PER_PAGE } from '../constants'
import { AlgoliaRequest } from '../types/retail-media'

type ShopperProduct = ShopperProducts.Product

export interface Promotion {
  ID: string
  basedOnCustomerGroups: string[]
  isMealDealLanding: boolean
  msg: string
  multibuyUnitPrice: string
  req: number
  startDate: string
  t: string
  details?: object
  finished?: boolean
  groups?: Array<{
    cartQty?: number
    requiredQty?: number
    req?: string[]
    ids?: string[]
    t?: string
  }>
  imageURL?: string
  maxApp?: number
  name?: string
  promoQtyRatio?: number
  remainingQty?: number
  type?: string
  isCompleted?: boolean
  promotionPrice?: number
  buyXReq?: number
  getYQuantity?: number
}

export interface PromotionalBadgeText {
  t: string
  msg: string
}

export interface StoreProduct
  extends OmitFromKnownKeys<
    ShopperProduct,
    'variationAttributes' | 'variationValues' | 'variants' | 'productPromotions' | 'price'
  > {
  productPromotions: {
    multiBuyPromo: Promotion
    nearPromos: Array<Promotion>
  }
  priceInfo: {
    [price: number]: string
    perUnit: string
    wasPrice: string
  }
  price: {
    GBP: number
  }
}

export interface ISearchParams<TRefine = string[]> {
  organizationId?: string
  siteId?: string
  q?: string
  refine?: TRefine
  sort?: string
  currency?: string
  locale?: string
  offset?: any
  limit?: number
}

export interface Hit extends BaseItem, Record<string, unknown> {
  id: string
  name: string
  image_groups?: Array<{
    images?: Array<any>
  }>
  price?: {GBP: number}
  priceInfo?: {
    perUnit: string
    [price: number]: string
    wasPrice: string
  }
  productRating?: number
  pdpTopRightTileBadge?: string
  pdpMiddleRightTileBadge?: string
  pdpBottomRightTileBadge?: string
  pdpBadgeAsset?: string
  productPromotions: {
    multiBuyPromo: Promotion
    nearPromos: Array<Promotion>
  },
  matrixAvailability: Record<string, string[]>,
  matrixPrices: Record<string, string[]>
  maxOrderQuantity?: number
  isAddToCartDisabled: boolean
  disabledStatus: string | null
  productClasses: string[]
  objectID: string
  availableForDayAtStore: boolean
  __position: number
  __queryID: string
}

export interface TransformedHit extends Hit {
  defaultPrice: string
  dailyPrice: string | null
  displayPrice: string
}

interface InitialState {
  loading: boolean
  productsById: Record<string, ProductModel>
  lastHitResponse: AlgoliaResponse | object
  lastHitKey: string
}

export class ProductModel extends Wrapper<StoreProduct>() {
  constructor(product: StoreProduct) {
    super(product)
    makeObservable(this, {
      getImagesForStandardProduct: action.bound,
      getProductPromotionsIds: action.bound,
    })
  }

  getImagesForStandardProduct() {
    if (!this.imageGroups || this.imageGroups.length < 1) {
      return []
    }

    const foundGroup = this.imageGroups.find((group) => group.viewType === 'main')

    const imageSet = foundGroup || this.imageGroups[0]
    const images =
      (imageSet && imageSet.images) || (this.imageGroups[0] && this.imageGroups[0].images)
    return images || []
  }

  getProductPromotionsIds() {
    const promotions = Object.values(this.productPromotions).filter(Boolean)

    if (!promotions.length) return []

    const ids = promotions
      .map((promotion) => (Array.isArray(promotion) ? promotion?.map(({ID}) => ID) : promotion?.ID))
      .flat()
      .filter(Boolean)

    const uniqIds = [...new Set(ids)]

    return uniqIds
  }
}

export class ProductStore implements StoreModel {
  api: CommerceAPI
  rootStore: RootStore
  currentSearchKey?: string | null
  loading: boolean
  productsById: Record<string, ProductModel>
  lastHitKey?: string
  lastHitResponse?: AlgoliaResponse | object

  constructor(
    rootStore: RootStore,
    initialState: InitialState = {
      loading: false,
      productsById: {},
      lastHitKey: '',
      lastHitResponse: {}
    },
  ) {
    this.loading = initialState.loading
    this.lastHitKey = ''
    this.lastHitResponse = {}
    this.productsById = Object.keys(initialState.productsById || []).reduce((acc, id) => {
      return {...acc, [id]: new ProductModel(initialState.productsById[id])}
    }, {})

    makeObservable(this, {
      loading: observable,
      productsById: observable,
      lastHitKey: observable,
      lastHitResponse: observable,
      fetchProducts: flow.bound,
      fetchSponsoredHits: flow.bound,
      productSearchWithSponsored: flow.bound,
      storeHitsAsProducts: flow.bound,
      getPdpAwardSlot: flow.bound,
      getItemPrice: action.bound
    })

    this.api = rootStore.api
    this.rootStore = rootStore
  }

  get asJson() {
    return {
      lastHitKey: this.lastHitKey,
      lastHitResponse: this.lastHitResponse,
      productsById: this.productsById,
    }
  }

  /**
   * Takes a list of product IDs and fetches any we don't already have in the store
   * @param {Array<string>} ids - array of IDs to fetch
   * @param {boolean} ignoreRange
   * @returns {string[]} fetchedIds - a list of all IDs found in the productResults, if no valid IDs passed in ids param, will return a blank array
   */
  *fetchProducts(ids: Array<string>, ignoreRange: boolean) {
    try {
      /* 
      Filter out ids for products we have already fetched.
      We may have a partial product in state but still need the full detail.
      We consider a product with a `variants` field to be fully fetched, so we check for that.
      */
      const filteredIds = ids.filter((id) => {
        const existingProduct = this.productsById[id]
        return !existingProduct
      })

      let fetchedIds: string[] = []
      if (filteredIds.length > 0) {
        // Fetch the products we don't have from the API 
        let productResults: Hit[] = yield this.api.algoliaShopperProduct.getProducts({
          parameters: {ids: filteredIds.toString(), allImages: true, perPricebook: true, ignoreRange:ignoreRange, day: this.rootStore.globalStore.selectedAvailabilityDayOffset, store: this.rootStore.globalStore.selectedStoreId},
        })

        // Filter null results out
        productResults = productResults ? productResults.filter(value => value !== null) : []

        // Update list of IDs to return
        fetchedIds = productResults.map((product) => product.id)

        // Store any returned products into the Product Store
        this.storeHitsAsProducts(productResults)
      }
      return fetchedIds
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  /**
   * Caches the last Algolia Response 
   * @param {string} key the search key
   * @param {AlgoliaResponse} algoliaResponse the Algolia Response
  */
  storeHitsResult(key: string, result: AlgoliaResponse) {
    this.lastHitKey = key
    this.lastHitResponse = result
  }

  /**
   * Checks if the last fetch search result matches the current request and returns if so
   * @param {string} key the search key
   * @returns {AlgoliaResponse} The fetched search response
  */
  getHitResult(key: string) {
    if (this.lastHitKey === key) {
      return this.lastHitResponse
    }
    return null
  }

  /**
   * Fetches sponsored products for current search values
   * @param {string} pageType Page type - e.g 'search' or 'category'
   * @param {string} key the search key
   * @returns {RetailMediaSponsoredProductResponse} The fetched search response
   */
  *fetchSponsoredHits(pageType: string, key: string) {
    const retailMediaResponse: RetailMediaResponse = yield this.rootStore.retailMediaStore.fetchProductAds(pageType, key)
    const productAds: RetailMediaProduct[] = (retailMediaResponse?.ads || [])
    const sponsoredProductIds = productAds.map(ad => ad.gtin).filter(Boolean)
    const sponsoredAdIds = productAds.map(ad => ({
        id: ad.gtin,
        adId: ad.id
    }))

    if (!sponsoredProductIds.length) {
      return { hits: [], ids: []}
    }

    // Fetch product data for our sponsored product IDs
    yield flowResult(this.fetchProducts(sponsoredProductIds, false))

    let sponsoredHits: SponsoredHit[] = []
    const matchedIds: string[] = []
    sponsoredProductIds.forEach((id, index) => {
      const product: ProductModel = this.productsById[id]
      if (product) {
        matchedIds.push(id)
        sponsoredHits.push(product)
      }
    })

    // Configure sponsored products for search results
    sponsoredHits = sponsoredHits.map(hit => ({
        ...hit,
        isSponsored: true,
        adId: sponsoredAdIds.find(x => x.id === hit.id)?.adId || ''
    }))

    return { hits: sponsoredHits, ids: matchedIds }
  }


  /**
   * Completes a search request containing both Algolia and Retail Media products
   * @param {SponsoredProductRequestProps} config Config data for search
   * @param {object} requests The search request
   * @returns {AlgoliaResponse} The fetched search response
   */
  *productSearchWithSponsored(
    config: SponsoredProductRequestProps,
    requests: AlgoliaRequest[]
  ) {
      try {
        const { selectedAvailabilityDayOffset, selectedStoreId, pageType, filterValue } = config

        const key: string = JSON.stringify({ config, requests })

        const cached = this.getHitResult(key)

        const {algoliaIndexBase} = this.rootStore.globalStore

        if (cached) {
          return cached
        }

        const availabilityOffset = selectedAvailabilityDayOffset || 1
        const storeId = selectedStoreId || '0'

        const searchFilters = requests?.length && requests[0].params?.facetFilters?.length ? requests[0].params?.facetFilters : []

        // Fetch Sponsored Product IDS as long as the user hasn't filtered anything
        const hasFilterOrSortRules = requests[0]?.indexName !== (`${algoliaIndexBase}__products__default`) || searchFilters.length
        const sponsoredProducts: RetailMediaSponsoredProductResponse = hasFilterOrSortRules ? {} : yield this.fetchSponsoredHits(pageType, filterValue)
        const sponsoredHits = sponsoredProducts?.hits || []
        const sponsoredIds = sponsoredProducts?.ids || []

        // Fetch Organic Algolia products (filtering out our pre-fetched sponsored IDs)
        const currentPage = requests[0]?.params.page || 0
        const algoliaResponse: Response = yield fetch(`/algolia/organicProducts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ requests: requests, config: {
            ...config,
            filters: searchFilters,
            selectedAvailabilityDayOffset: availabilityOffset,
            selectedStoreId: storeId,
            sponsoredProductIds: sponsoredIds,
            page: currentPage,
            searchIndex: requests[0].indexName
          }})
        })

        const algoliaJson: OrganicProductFetchResult = yield algoliaResponse.json()
        const organicHits = algoliaJson.filteredHits || []

        const mergedHits = currentPage === 0 ? [...sponsoredHits, ...organicHits].slice(0, HITS_PER_PAGE) : organicHits
        // Merge organic + Sponsored and limit to correct number per page
        const totalHits = algoliaJson.algoliaResponse?.nbHits || mergedHits.length

        // Return combined result
        const response: AlgoliaResponse = {
            results: [
              {
                ...algoliaJson.algoliaResponse || {},
                hits: mergedHits,
                nbHits: totalHits,
                nbPages: Math.ceil(totalHits/HITS_PER_PAGE),
                page: requests[0].params.page,
                data: {
                  sponsoredCount: sponsoredHits.length,
                  organicCount: organicHits.length

                }
              }
            ]
          }

          this.storeHitsResult(key, response)

          return response
    } catch (error) {
      console.error(error)
    }
    return {}
  }

  // Takes an Array of Hit objects from Algolia and converts them to ProductModels
  // Then stores them in the Product Store for later use
  *storeHitsAsProducts(products: Array<Hit>) {
    const promotionIds: string[] = []

    for (const product of products) {
      const _product = keysToCamel(product)

      if (_product.id) {
        // Convert the Hit into our Product Model
        const productModel = new ProductModel({
          ...this.productsById[_product.id],
          ..._product,
        })
        this.productsById[product.id] = productModel

        // Check for any promotions for this product so we can fetch Promo data in advance
        const productPromotionIds = productModel.getProductPromotionsIds()
        promotionIds.push(...productPromotionIds)
      }
    }

    // Fetch any promotion data we don't already have
    yield this.rootStore.promotionStore.fetchPromotionsByIds([...new Set(promotionIds)])
  }

  *getPdpAwardSlot(productId: string, categoryIds: string[]) {
    const {contentStore} = this.rootStore

    if (contentStore.pdpAwardSlots.length === 0) {
      yield contentStore.fetchItemsByContentType( 'https://www.iceland.co.uk/slot/pdp-awards-slot.json', 'pdpAwardSlots')
    }

    // Find content on Product Level first, then if there is no content on Product Level, find content on Category Level
    const relevantProductLevelContent = contentStore.pdpAwardSlots.find((slot) => {
      return slot.productList?.products?.some((product) => product.id === productId)
    })

    if (relevantProductLevelContent) {
      return relevantProductLevelContent
    }

    const relevantCategoryLevelContent = contentStore.pdpAwardSlots.find((slot) => {
      return slot.categoryList?.some((category) => categoryIds.includes(category.id))
    })

    if (relevantCategoryLevelContent) {
      return relevantCategoryLevelContent
    }

    return null
  }

  getItemPrice(hit: Nullable<ProductModel>) {
    const selectedPriceDayOffset = this.rootStore.globalStore.selectedPriceDayOffset
    const selectedRegion = this.rootStore.globalStore.selectedRegion
    const getApplicablePromotions = this.rootStore.customerStore.getApplicablePromotions
    const {onlineExclusivePromo} = getApplicablePromotions(hit?.productPromotions)
    let displayPrice:Nullable<number> = null
    let promotionPrice:Nullable<number> = null

    if(hit) {
      // Gets an array of prices for that day indexed by region, take day one if no selectedPriceDay
      displayPrice = selectedPriceDayOffset != null
      ? hit?.matrixPrices?.[selectedPriceDayOffset]?.[selectedRegion]
      : null

      if (displayPrice === null) {
        displayPrice = hit?.matrixPrices?.[0]?.[0]
      }

      if (onlineExclusivePromo && onlineExclusivePromo.promotionPrice !== undefined) {
        promotionPrice = onlineExclusivePromo.promotionPrice
      }
    }
    return {displayPrice, promotionPrice}
  }
}
